import {
  addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, Timestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { chunk } from '../db';

// Waitlist administration. Visitors join through the joinWaitlist callable (accounts.js).

const withCreatedDate = (snap) => ({
  id: snap.id,
  ...snap.data(),
  createdAt: snap.data().createdAt?.toDate?.() || new Date(),
});

/**
 * Every waitlist entry, newest first, with createdAt as a Date. Sorted here rather than
 * with orderBy so entries without createdAt (old imports) are included, last.
 */
export async function listWaitlist() {
  const snapshot = await getDocs(collection(db, 'waitlist'));
  const millis = (snap) => snap.data().createdAt?.toMillis?.() || 0;
  return [...snapshot.docs].sort((a, b) => millis(b) - millis(a)).map(withCreatedDate);
}

/** Waitlist survey answers, newest first. */
export async function listSurveyResponses() {
  const snapshot = await getDocs(query(collection(db, 'survey_responses'), orderBy('createdAt', 'desc')));
  return snapshot.docs.map(withCreatedDate);
}

export function updateWaitlistEntry(id, data) {
  return updateDoc(doc(db, 'waitlist', id), { ...data, updatedAt: Timestamp.now() });
}

export function deleteWaitlistEntry(id) {
  return deleteDoc(doc(db, 'waitlist', id));
}

/** Flag the registered profiles with these emails as invited. */
export async function markUsersInvited(emails) {
  const lowered = emails.map((email) => (email || '').toLowerCase()).filter(Boolean);
  const batches = await Promise.all(chunk(lowered, 30).map((ids) =>
    getDocs(query(collection(db, 'users'), where('email', 'in', ids)))));
  await Promise.all(batches.flatMap((snapshot) => snapshot.docs.map((userDoc) =>
    updateDoc(userDoc.ref, { isInvited: true, updatedAt: Timestamp.now() }))));
}

// ─── Trigger Email extension ("mail") ───────────────────────────────

/** Queue an email: { to, message: { subject, html } }. */
export function queueMail(mail) {
  return addDoc(collection(db, 'mail'), { ...mail, createdAt: Timestamp.now() });
}

/** Of these emails (lower-cased), the ones that have had a mail delivered successfully. */
export async function deliveredEmails(emails) {
  const lowered = emails.map((email) => (email || '').toLowerCase()).filter(Boolean);
  const delivered = new Set();
  for (const ids of chunk(lowered, 30)) {
    const snapshot = await getDocs(query(collection(db, 'mail'), where('to', 'in', ids)));
    snapshot.forEach((snap) => {
      const data = snap.data() || {};
      const to = (data.to || '').toLowerCase();
      const status = (data.delivered?.status || data.delivery?.status || data.delivery?.state || '').toLowerCase();
      if (to && status === 'success') delivered.add(to);
    });
  }
  return delivered;
}
